import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Database, Download, RotateCcw, Search, Trash2, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { dataCubeApi } from '@/api/data-cube'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { DateRangePicker } from '@/components/DateRangePicker'
import { DepartmentScopeFilter } from '@/components/department-scope-filter/DepartmentScopeFilter'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { showError } from '@/lib/error'
import type { DataCubeListParams } from '@/types/data-cube'

const metricColumns = [
	{ key: 'friend_count', label: '好友' },
	{ key: 'crony_count', label: '加上的好友' },
	{ key: 'breaking_count', label: '破冰' },
	{ key: 'daily_count', label: '一天生活' },
	{ key: 'location_count', label: '定位' },
	{ key: 'gift_count', label: '礼物' },
	{ key: 'medical_count', label: '义诊' },
	{ key: 'photo_count', label: '素颜照' },
	{ key: 'newly_count', label: '新开' },
] as const

export function RecordsPage() {
	const queryClient = useQueryClient()
	const inputRef = useRef<HTMLInputElement>(null)
	const [filters, setFilters] = useState<DataCubeListParams>({ scope: 'my' })
	const [searchFilters, setSearchFilters] = useState<DataCubeListParams>({ scope: 'my' })
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
	const [selected, setSelected] = useState<number[]>([])

	const { data: batches = [] } = useQuery({ queryKey: ['data-cube', 'batches'], queryFn: dataCubeApi.batches })
	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['data-cube', 'records', searchFilters, page, pageSize],
		queryFn: () => dataCubeApi.records({ ...searchFilters, page, page_size: pageSize, sort: 'id', order: 'descending' }),
	})

	const invalidate = () => {
		queryClient.invalidateQueries({ queryKey: ['data-cube'] })
		setSelected([])
	}

	const importMutation = useMutation({
		mutationFn: (file: File) => dataCubeApi.import(file),
		onSuccess: (result) => {
			invalidate()
			toast.success(`导入完成：成功 ${result.success} 条，失败 ${result.failed} 条`)
		},
		onError: (error) => showError(error, '导入失败'),
	})

	const deleteMutation = useMutation({
		mutationFn: (id: number) => dataCubeApi.deleteRecord(id),
		onSuccess: () => {
			invalidate()
			toast.success('已删除')
		},
		onError: (error) => showError(error, '删除失败'),
	})

	const batchDeleteMutation = useMutation({
		mutationFn: (ids: number[]) => dataCubeApi.batchDeleteRecords(ids),
		onSuccess: () => {
			invalidate()
			toast.success('批量删除成功')
		},
		onError: (error) => showError(error, '批量删除失败'),
	})

	const records = data?.list ?? []
	const allSelected = records.length > 0 && records.every((item) => selected.includes(item.id))
	const pendingDelete = deleteMutation.isPending || batchDeleteMutation.isPending

	const handleSearch = () => {
		setSearchFilters(cleanParams(filters))
		setPage(1)
		setSelected([])
	}
	const handleReset = () => {
		setFilters({ scope: 'my' })
		setSearchFilters({ scope: 'my' })
		setPage(1)
		setSelected([])
	}
	const handlePageChange = (nextPage: number) => {
		setPage(nextPage)
		setSelected([])
	}
	const handlePageSizeChange = (nextPageSize: number) => {
		setPageSize(nextPageSize)
		setSelected([])
	}
	const handleDelete = (id: number) => {
		if (!window.confirm('确认删除这条数据魔方记录吗？')) return
		deleteMutation.mutate(id)
	}
	const handleBatchDelete = () => {
		const currentSelected = records.filter((record) => selected.includes(record.id)).map((record) => record.id)
		if (currentSelected.length === 0) return
		if (!window.confirm(`确认删除选中的 ${currentSelected.length} 条数据魔方记录吗？`)) return
		batchDeleteMutation.mutate(currentSelected)
	}
	const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		event.target.value = ''
		if (file) importMutation.mutate(file)
	}

	return (
		<div className="space-y-5">
			<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">数据魔方</h1>
					<p className="mt-1 text-sm text-muted-foreground">导入并统计员工运营指标</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<PermissionGuard permission="data_cube:import">
						<Button type="button" variant="outline" onClick={() => dataCubeApi.template()}>
							<Download className="mr-1.5 h-4 w-4" />
							下载模板
						</Button>
						<Button type="button" disabled={importMutation.isPending} onClick={() => inputRef.current?.click()}>
							<Upload className="mr-1.5 h-4 w-4" />
							导入 Excel
						</Button>
						<input ref={inputRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={handleFile} />
					</PermissionGuard>
				</div>
			</div>

			<Card className="rounded-lg">
				<CardContent className="pt-6">
					<div className="flex flex-wrap items-center gap-3">
						<Select value={filters.batch_id ? String(filters.batch_id) : 'all'} onValueChange={(value) => setFilters({ ...filters, batch_id: value === 'all' ? undefined : Number(value) })}>
							<SelectTrigger className="w-[190px]">
								<SelectValue placeholder="全部批次" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">全部批次</SelectItem>
								{batches.map((batch) => (
									<SelectItem key={batch.id} value={String(batch.id)}>
										{batch.batch_name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					<DepartmentScopeFilter value={{ scope: filters.scope || 'my', department_id: filters.department_id }} onChange={(scopeValue) => setFilters({ ...filters, department_id: undefined, ...scopeValue, /* 本页未启用「无归属」 */ scope: scopeValue.scope === 'unassigned' ? undefined : scopeValue.scope })} />
						<DateRangePicker value={{ start_date: filters.start_date, end_date: filters.end_date }} onChange={(value) => setFilters({ ...filters, ...value })} />
						<Button type="button" onClick={handleSearch} disabled={isFetching}>
							<Search className="mr-1.5 h-4 w-4" />
							查询
						</Button>
						<Button type="button" variant="outline" onClick={handleReset}>
							<RotateCcw className="mr-1.5 h-4 w-4" />
							重置
						</Button>
					</div>
				</CardContent>
			</Card>

			<Card className="overflow-hidden rounded-lg">
				<CardContent className="p-0">
					{selected.length > 0 && (
						<div className="flex items-center justify-between border-b px-4 py-3">
							<span className="text-sm text-muted-foreground">已选择 {selected.length} 条记录</span>
							<PermissionGuard permission="data_cube:delete">
								<Button type="button" size="sm" variant="outline" disabled={pendingDelete} onClick={handleBatchDelete}>
									<Trash2 className="mr-1.5 h-3.5 w-3.5" />
									批量删除
								</Button>
							</PermissionGuard>
						</div>
					)}
					<div className="overflow-x-auto">
						<Table className="min-w-[1180px]">
							<TableHeader>
								<TableRow>
									<TableHead className="w-12">
										<Checkbox checked={allSelected} onCheckedChange={(checked) => setSelected(checked === true ? records.map((item) => item.id) : [])} />
									</TableHead>
									<TableHead className="w-20">ID</TableHead>
									<TableHead className="w-36">员工</TableHead>
									{metricColumns.map((column) => <TableHead key={column.key}>{column.label}</TableHead>)}
									<TableHead className="w-40">录入时间</TableHead>
									<TableHead className="w-24 text-right">操作</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{isLoading ? (
									<TableRow>
										<TableCell colSpan={14} className="h-28 text-center">加载中...</TableCell>
									</TableRow>
								) : records.length === 0 ? (
									<TableRow>
										<TableCell colSpan={14} className="h-32 text-center text-muted-foreground">
											<Database className="mx-auto mb-2 h-8 w-8" />
											暂无数据魔方记录
										</TableCell>
									</TableRow>
								) : records.map((record) => (
									<TableRow key={record.id}>
										<TableCell>
											<Checkbox checked={selected.includes(record.id)} onCheckedChange={(checked) => setSelected((current) => checked === true ? [...current, record.id] : current.filter((id) => id !== record.id))} />
										</TableCell>
										<TableCell>{record.id}</TableCell>
										<TableCell className="font-medium">{record.user?.nickname || record.user?.username || record.user_id}</TableCell>
										{metricColumns.map((column) => <TableCell key={column.key}>{record[column.key] ?? 0}</TableCell>)}
										<TableCell>{formatDateTime(record.created_at)}</TableCell>
										<TableCell className="text-right">
											<PermissionGuard permission="data_cube:delete">
												<Button type="button" size="sm" variant="ghost" disabled={pendingDelete} onClick={() => handleDelete(record.id)}>
													<Trash2 className="h-4 w-4" />
													<span className="sr-only">删除</span>
												</Button>
											</PermissionGuard>
										</TableCell>
									</TableRow>
								))}
								{data?.summary && (
									<TableRow className="bg-muted/40 font-medium">
										<TableCell />
										<TableCell>汇总</TableCell>
										<TableCell />
										{metricColumns.map((column) => <TableCell key={column.key}>{data.summary[column.key] ?? 0}</TableCell>)}
										<TableCell />
										<TableCell />
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
				</CardContent>
			</Card>

			<ListPagination total={data?.total ?? 0} page={page} pageSize={pageSize} loading={isFetching} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} />
		</div>
	)
}

function cleanParams(params: DataCubeListParams): DataCubeListParams {
	return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== '')) as DataCubeListParams
}

function formatDateTime(value?: string) {
	if (!value) return '-'
	return value.replace('T', ' ').slice(0, 16)
}
